// =============================================================================
// COMMERCE MODE
// Syncs attribution onto the Shopify cart (`POST /cart/update.js`) so it
// survives into checkout as `note_attributes`, which the `orders/paid`
// webhook reads to tie a purchase back to the RL arm that drove it.
// =============================================================================

import type { AttributionRecord } from './types';

const CART_UPDATE_PATH = '/cart/update.js';
const LAST_HASH_KEY = '_ab_commerce_hash';
const LAST_SYNC_AT_KEY = '_ab_commerce_synced_at';
const MIN_SYNC_INTERVAL_MS = 30_000;
const CART_ADD_PATTERN = /\/cart\/add(\.js)?(\?|$)/;

type TrackFn = (name: string, properties?: Record<string, unknown>) => void;

export interface InstallCommerceModeOptions {
  visitorId: string;
  sessionId: string;
  /** Returns the current merged attribution record (first-touch + last-touch). */
  getAttribution: () => AttributionRecord | null | undefined;
  /** Emits a `track` event — used for `add_to_cart` and `view_content`. */
  track?: TrackFn;
  debug?: boolean;
}

export interface CommerceModeHandle {
  /** Re-checks attribution and syncs the cart if the payload changed and the throttle allows it. */
  syncCart: () => Promise<void>;
}

/**
 * True when the page is a Shopify storefront: either the script tag opts in
 * explicitly (`data-commerce="shopify"`) or `window.Shopify` is present.
 */
export function isCommerceMode(): boolean {
  if (typeof window === 'undefined') return false;
  const script = document.querySelector<HTMLScriptElement>('script[data-commerce]');
  if (script?.getAttribute('data-commerce') === 'shopify') return true;
  return Boolean((window as unknown as { Shopify?: unknown }).Shopify);
}

/**
 * Maps the SDK's internal attribution record to the `ab_*` note-attribute
 * names the `orders/paid` webhook reads off the Shopify order.
 */
export function buildCartAttributes(
  visitorId: string,
  sessionId: string,
  attribution: AttributionRecord | null | undefined
): Record<string, string> {
  const attrs: Record<string, string> = {
    ab_visitor: visitorId,
    ab_session: sessionId,
  };

  if (!attribution) return attrs;
  if (attribution.ab_project) attrs.ab_project = attribution.ab_project;
  if (attribution.ab_variant) attrs.ab_variant = attribution.ab_variant;
  if (attribution.utm_campaign) attrs.ab_campaign = attribution.utm_campaign;
  if (attribution.fbclid) attrs.ab_fbclid = attribution.fbclid;
  if (attribution.ttclid) attrs.ab_ttclid = attribution.ttclid;
  if (attribution.gclid) attrs.ab_gclid = attribution.gclid;

  return attrs;
}

function hashPayload(attributes: Record<string, string>): string {
  const canonical = JSON.stringify(attributes, Object.keys(attributes).sort());
  let hash = 0;
  for (let i = 0; i < canonical.length; i++) {
    hash = (hash * 31 + canonical.charCodeAt(i)) | 0;
  }
  return String(hash);
}

function readLocal(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function writeLocal(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch {
    // localStorage might be disabled (private mode) — sync still happens, just isn't deduped.
  }
}

// Holds the most recently installed track callback so the (idempotently
// patched, page-lifetime-long) fetch/XHR interceptors always report through
// the current installCommerceMode() caller rather than a stale closure.
let activeTrack: TrackFn | undefined;

function requestUrl(input: RequestInfo | URL): string {
  if (typeof input === 'string') return input;
  if (input instanceof URL) return input.toString();
  return (input as Request).url;
}

function reportAddToCart(data: unknown): void {
  if (!activeTrack || !data || typeof data !== 'object') return;
  const record = data as Record<string, unknown>;
  const item = Array.isArray(record.items)
    ? (record.items[0] as Record<string, unknown> | undefined)
    : record;
  if (!item) return;

  activeTrack('add_to_cart', {
    product_id: item.product_id,
    variant_id: item.variant_id,
    price: item.price,
    quantity: item.quantity,
  });
}

/** Wraps `window.fetch` once (idempotent) to report successful `/cart/add(.js)` calls. */
function patchFetchForAddToCart(): void {
  if (typeof window === 'undefined' || typeof window.fetch !== 'function') return;
  const target = window.fetch as typeof window.fetch & { __abPatched?: boolean };
  if (target.__abPatched) return;

  const originalFetch = window.fetch.bind(window);
  const patched = ((input: RequestInfo | URL, init?: RequestInit) => {
    const result = originalFetch(input, init);
    result
      .then((response) => {
        try {
          if (response.ok && CART_ADD_PATTERN.test(requestUrl(input))) {
            response
              .clone()
              .json()
              .then(reportAddToCart)
              .catch(() => {});
          }
        } catch {
          // never let interception affect the real request
        }
      })
      .catch(() => {});
    return result;
  }) as typeof window.fetch & { __abPatched?: boolean };
  patched.__abPatched = true;

  window.fetch = patched;
}

/** Wraps `XMLHttpRequest.prototype.open/send` once (idempotent) for `/cart/add(.js)` calls. */
function patchXhrForAddToCart(): void {
  if (typeof XMLHttpRequest === 'undefined') return;
  const proto = XMLHttpRequest.prototype as XMLHttpRequest & { __abPatched?: boolean };
  if (proto.__abPatched) return;

  const originalOpen = XMLHttpRequest.prototype.open;
  const originalSend = XMLHttpRequest.prototype.send;

  XMLHttpRequest.prototype.open = function (this: XMLHttpRequest, ...args: unknown[]) {
    (this as unknown as { __abUrl?: string }).__abUrl = String(args[1] ?? '');
    return (originalOpen as (...a: unknown[]) => unknown).apply(this, args);
  } as typeof XMLHttpRequest.prototype.open;

  XMLHttpRequest.prototype.send = function (this: XMLHttpRequest, ...args: unknown[]) {
    const url = (this as unknown as { __abUrl?: string }).__abUrl;
    if (url && CART_ADD_PATTERN.test(url)) {
      this.addEventListener('load', () => {
        try {
          if (this.status >= 200 && this.status < 300) {
            reportAddToCart(JSON.parse(this.responseText));
          }
        } catch {
          // never let interception affect the real request
        }
      });
    }
    return (originalSend as (...a: unknown[]) => unknown).apply(this, args);
  } as typeof XMLHttpRequest.prototype.send;

  proto.__abPatched = true;
}

function extractProductHandle(pathname: string): string | null {
  const match = pathname.match(/\/products\/([^/?#]+)/);
  return match?.[1] ?? null;
}

/** Tracks `view_content` once, if the current page is a Shopify product page. */
function trackViewContent(track?: TrackFn): void {
  if (typeof window === 'undefined' || !track) return;
  const handle = extractProductHandle(window.location.pathname);
  if (handle) track('view_content', { product_handle: handle });
}

/**
 * Installs commerce mode: on Shopify pages, syncs the visitor id, session id,
 * and available attribution onto the cart. No-ops entirely off-Shopify or
 * without `fetch`. Every network call is fire-and-forget and never throws;
 * a failed sync (404/500/network error) disables further syncs for the
 * session so a broken storefront doesn't retry forever.
 */
export function installCommerceMode(options: InstallCommerceModeOptions): CommerceModeHandle {
  let disabled = false;

  async function syncCart(): Promise<void> {
    if (disabled) return;
    if (!isCommerceMode()) return;
    if (typeof fetch === 'undefined') return;

    let attribution: AttributionRecord | null | undefined;
    try {
      attribution = options.getAttribution();
    } catch {
      attribution = null;
    }

    const attributes = buildCartAttributes(options.visitorId, options.sessionId, attribution);
    const hash = hashPayload(attributes);

    if (readLocal(LAST_HASH_KEY) === hash) return;

    const now = Date.now();
    const lastSyncAt = Number(readLocal(LAST_SYNC_AT_KEY) ?? 0);
    if (now - lastSyncAt < MIN_SYNC_INTERVAL_MS) return;

    try {
      const response = await fetch(CART_UPDATE_PATH, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ attributes }),
      });

      if (!response.ok) {
        disabled = true;
        if (options.debug) {
          console.warn('[AllBrainer] commerce cart sync disabled after status', response.status);
        }
        return;
      }

      writeLocal(LAST_HASH_KEY, hash);
      writeLocal(LAST_SYNC_AT_KEY, String(now));
    } catch (err) {
      disabled = true;
      if (options.debug) {
        console.warn('[AllBrainer] commerce cart sync failed, disabling for session', err);
      }
    }
  }

  if (isCommerceMode()) {
    activeTrack = options.track;
    patchFetchForAddToCart();
    patchXhrForAddToCart();
    trackViewContent(options.track);

    if (typeof fetch !== 'undefined') {
      void syncCart();
    }
  }

  return { syncCart };
}
